import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import usePermission from '../../hooks/usePermission';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  Plus, Eye, Trash2, ClipboardCheck, RefreshCw, X, ChevronRight, Loader2
} from 'lucide-react';
import ProjectSelect from '../../components/ProjectSelect';

const STATUS_BADGES = {
  draft:     'bg-gray-100 text-gray-700 border-gray-300',
  assigned:  'bg-blue-100 text-blue-700 border-blue-300',
  submitted: 'bg-amber-100 text-amber-700 border-amber-300',
  approved:  'bg-green-100 text-green-700 border-green-300',
  rejected:  'bg-red-100 text-red-700 border-red-300',
};
const STATUS_LABELS = { draft: 'Draft', assigned: 'Assigned', submitted: 'Pending Approval', approved: 'Approved', rejected: 'Rejected' };
const TABS = ['all', 'draft', 'assigned', 'submitted', 'approved', 'rejected'];

export default function QualityReportListPage() {
  const { canWrite, canDelete } = usePermission('quality_report');
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/worker') ? '/worker/quality' : '/admin/quality';

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/quality/reports', { params: { status } });
      setRows(res.data.rows || []);
      const map = {};
      (res.data.counts || []).forEach(c => { map[c.status] = c.c; });
      map.all = Object.values(map).reduce((a, b) => a + b, 0);
      setCounts(map);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load reports');
    } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    try {
      await api.delete(`/quality/reports/${deleteTarget.id}`);
      toast.success('Report deleted'); setDeleteTarget(null); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <ClipboardCheck className="w-6 h-6 text-blue-600" /> Quality Reports
          </h1>
          <p className="text-sm text-gray-500">Product quality inspection reports with dynamic sections &amp; images</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50" title="Refresh">
            <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {canWrite && (
            <button onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">
              <Plus className="w-4 h-4" /> New Report
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map(t => (
          <button key={t} onClick={() => setStatus(t)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              status === t ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
            {t === 'all' ? 'All' : STATUS_LABELS[t]} {counts[t] !== undefined ? `(${counts[t]})` : ''}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        {loading ? <div className="p-10 text-center text-gray-400 text-sm">Loading…</div>
          : rows.length === 0 ? <div className="p-10 text-center text-gray-400 text-sm">No reports found</div>
          : (
            <table className="w-full text-sm min-w-[840px]">
              <thead><tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <th className="px-4 py-3">Report No.</th><th className="px-4 py-3">Project</th>
                <th className="px-4 py-3">Client</th><th className="px-4 py-3">SKU</th>
                <th className="px-4 py-3">Level</th><th className="px-4 py-3 text-center">Sections</th>
                <th className="px-4 py-3 text-center">Images</th><th className="px-4 py-3">Assigned</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`${basePath}/${r.id}`)}>
                    <td className="px-4 py-3 font-semibold text-blue-700">{r.report_no}</td>
                    <td className="px-4 py-3">{r.project_code || r.project_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{r.client_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{r.sku_number || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 capitalize">{(r.inspection_level || '').replace('_', ' ')}</td>
                    <td className="px-4 py-3 text-center">{r.section_count}</td>
                    <td className="px-4 py-3 text-center">{r.image_count}</td>
                    <td className="px-4 py-3 text-gray-600">{r.assigned_to_name || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full border text-xs font-semibold ${STATUS_BADGES[r.status]}`}>
                        {STATUS_LABELS[r.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex justify-end gap-1.5">
                        <button onClick={() => navigate(`${basePath}/${r.id}`)} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600"><Eye className="w-4 h-4" /></button>
                        {canDelete && r.status === 'draft' && (
                          <button onClick={() => setDeleteTarget(r)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                        )}
                        <ChevronRight className="w-4 h-4 text-gray-300 self-center" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {showCreate && <CreateReportModal basePath={basePath} onClose={() => setShowCreate(false)}
        onCreated={id => { setShowCreate(false); navigate(`${basePath}/${id}`); }} />}

      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 w-full max-w-sm space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-800">Delete Report?</h3>
              <button onClick={() => setDeleteTarget(null)}><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <p className="text-sm text-gray-600">Delete draft <strong>{deleteTarget.report_no}</strong>? Sections and images will be removed.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm rounded-lg border border-gray-200">Cancel</button>
              <button onClick={confirmDelete} className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white font-semibold">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const DEFAULT_SECTIONS = [
  'Product Drawing', 'Product Image', 'Front View', 'Side View', 'Back View',
  'Dimensions', 'Wood Finish Sample', 'Rub Test', 'Nails Test', 'Smell Test',
];

function CreateReportModal({ onClose, onCreated }) {
  const [projects, setProjects] = useState([]);
  const [items, setItems] = useState([]);
  const [qcPersons, setQcPersons] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    project_id: '', project_item_id: '', client_name: '', sku_number: '',
    qc_person_id: '', inspection_level: 'final', inspection_date: new Date().toISOString().slice(0, 10),
    can_add_sections: false, useDefaults: true,
  });
  const set = p => setF(s => ({ ...s, ...p }));
  const [addingQc, setAddingQc] = useState(false);
  const [newQcName, setNewQcName] = useState('');
  const [savingQc, setSavingQc] = useState(false);

  const addQcPerson = async () => {
    const name = newQcName.trim();
    if (!name) return toast.error('Enter a name');
    setSavingQc(true);
    try {
      const res = await api.post('/quality/qc-persons', { name });
      const list = await api.get('/quality/qc-persons');
      setQcPersons(list.data);
      set({ qc_person_id: String(res.data.id) });
      setAddingQc(false); setNewQcName('');
      toast.success('QC person added');
    } catch (err) { toast.error(err.response?.data?.message || 'Could not add QC person'); }
    finally { setSavingQc(false); }
  };

  useEffect(() => {
    api.get('/projects').then(r => setProjects(Array.isArray(r.data) ? r.data : (r.data.rows || r.data.projects || []))).catch(() => {});
    api.get('/quality/qc-persons').then(r => setQcPersons(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!f.project_id) { setItems([]); return; }
    api.get(`/projects/${f.project_id}`).then(r => {
      const its = r.data.items || r.data.project_items || [];
      setItems(its);
      if (r.data.client_name && !f.client_name) set({ client_name: r.data.client_name });
    }).catch(() => setItems([]));
  }, [f.project_id]);

  const create = async () => {
    if (!f.project_id) return toast.error('Select a project');
    setSaving(true);
    try {
      const payload = { ...f };
      if (f.useDefaults) payload.sections = DEFAULT_SECTIONS.map(s => ({ section_name: s }));
      const res = await api.post('/quality/reports', payload);
      toast.success(`Created ${res.data.report_no}`);
      onCreated(res.data.id);
    } catch (err) { toast.error(err.response?.data?.message || 'Create failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-5 w-full max-w-lg space-y-3 max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-800">New Quality Report</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Project *">
            <ProjectSelect projects={projects} value={f.project_id} onChange={v => set({ project_id: v })}
              placeholder="— Select —" className="minp" />
          </F>
          <F label="Product / Item">
            <select className="minp" value={f.project_item_id} onChange={e => set({ project_item_id: e.target.value })}>
              <option value="">— Optional —</option>
              {items.map(it => <option key={it.id} value={it.id}>{it.item_name}{it.proto_code ? ` (${it.proto_code})` : ''}</option>)}
            </select>
          </F>
          <F label="Client Name"><input className="minp" value={f.client_name} onChange={e => set({ client_name: e.target.value })} /></F>
          <F label="SKU Number"><input className="minp" value={f.sku_number} onChange={e => set({ sku_number: e.target.value })} /></F>
          <F label="QC Person">
            {addingQc ? (
              <div className="flex gap-1">
                <input className="minp" autoFocus placeholder="QC person name" value={newQcName}
                  onChange={e => setNewQcName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addQcPerson(); } if (e.key === 'Escape') setAddingQc(false); }} />
                <button type="button" onClick={addQcPerson} disabled={savingQc}
                  className="px-2 rounded-lg bg-blue-600 text-white text-xs font-semibold disabled:opacity-50">
                  {savingQc ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Add'}
                </button>
                <button type="button" onClick={() => { setAddingQc(false); setNewQcName(''); }}
                  className="px-1.5 rounded-lg border text-gray-500"><X className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <select className="minp" value={f.qc_person_id}
                onChange={e => e.target.value === '__new__' ? setAddingQc(true) : set({ qc_person_id: e.target.value })}>
                <option value="">{qcPersons.length ? '— Select —' : '— No QC persons yet —'}</option>
                {qcPersons.map(q => <option key={q.id} value={q.id}>{q.name}</option>)}
                <option value="__new__">+ Add new QC person…</option>
              </select>
            )}
          </F>
          <F label="Inspection Level">
            <select className="minp" value={f.inspection_level} onChange={e => set({ inspection_level: e.target.value })}>
              <option value="final">Final</option>
              <option value="white_body">White Body</option>
            </select>
          </F>
          <F label="Inspection Date"><input className="minp" type="date" value={f.inspection_date} onChange={e => set({ inspection_date: e.target.value })} /></F>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.useDefaults} onChange={e => set({ useDefaults: e.target.checked })} />
          Pre-fill standard inspection sections (Drawing, Views, Dimensions, Finish, Tests…)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.can_add_sections} onChange={e => set({ can_add_sections: e.target.checked })} />
          Allow the assigned worker to add new sections
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-200">Cancel</button>
          <button onClick={create} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Create'}
          </button>
        </div>
      </div>
      <style>{`.minp{width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:8px;font-size:13px;} .minp:focus{outline:2px solid #bfdbfe;}`}</style>
    </div>
  );
}

function F({ label, children }) {
  return <div><label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>{children}</div>;
}
